import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Divider,
	Grid,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Modal,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { SinisterDocumentsService } from "../../../../shared/services/api/sinister/SinisterDocumentsService";
import { returnMessageError } from "../../../../shared/utils/returnMessageError";
import { ModalDocumentValueList } from "./ModalDocumentValueList";
import { ModalPreviewValue } from "./ModalPreviewValue";

export type ModalAllDocumentsValuesValueRow = {
	id?: string;
	sinisterId?: string;
	status?: string;
	typeId?: string;
	reference?: string;
	sinisterValuesDocuments?: any[];
};

type AggregatedValueDoc = {
	id: string;
	docType: string;
	mimetype?: string;
	filename?: string;
	originalFilename?: string;
	url?: string;
	uploadedDate?: string;
	originalName?: string;
	createdAt?: string;
	valueId?: string;
	valueStatus?: string;
	valueTypeId?: string;
	valueReference?: string;
};

interface ModalAllDocumentsValuesProps {
	open: boolean;
	sinisterId: string;
	valueRows: ModalAllDocumentsValuesValueRow[];
	onClose: () => void;
}

export const ModalAllDocumentsValues = ({ open, sinisterId, valueRows, onClose }: ModalAllDocumentsValuesProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [documents, setDocuments] = useState<AggregatedValueDoc[]>([]);
	const [previewDoc, setPreviewDoc] = useState<any | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [documentToDelete, setDocumentToDelete] = useState<any | null>(null);
	const [reloadToken, setReloadToken] = useState(0);
	const toastHandler = useToast();

	const valueIds = useMemo(() => {
		const unique = new Set<string>();
		for (const row of valueRows) {
			if (row?.id) unique.add(String(row.id));
		}
		return Array.from(unique);
	}, [valueRows]);

	const valueStatusById = useMemo(() => {
		const map = new Map<string, string>();
		for (const row of valueRows) {
			if (row?.id && row?.status) map.set(String(row.id), String(row.status));
		}
		return map;
	}, [valueRows]);

	const valueInfoById = useMemo(() => {
		const map = new Map<string, { typeId?: string; reference?: string }>();
		for (const row of valueRows) {
			if (!row?.id) continue;
			map.set(String(row.id), { typeId: row.typeId, reference: row.reference });
		}
		return map;
	}, [valueRows]);

	const statusKey = useMemo(() => {
		return valueIds.map((valueId) => `${valueId}:${valueStatusById.get(valueId) ?? ""}`).join("|");
	}, [valueIds, valueStatusById]);

	useEffect(() => {
		if (!open) return;
		let cancelled = false;

		const load = async () => {
			if (!sinisterId || valueIds.length === 0) {
				setDocuments([]);
				return;
			}

			setIsLoading(true);
			try {
				const settled = await Promise.allSettled(
					valueIds.map(async (valueId) => {
						const docs = await SinisterDocumentsService.getSinisterValuesDocumentTypes(sinisterId, valueId);
						return { valueId, docs };
					})
				);

				const aggregated: AggregatedValueDoc[] = [];
				for (const result of settled) {
					if (result.status !== "fulfilled") continue;
					const { valueId, docs } = result.value;
					const valueInfo = valueInfoById.get(valueId);
					for (const doc of docs ?? []) {
						aggregated.push({
							...doc,
							originalName: (doc as any)?.originalName ?? doc.originalFilename,
							createdAt: (doc as any)?.createdAt ?? doc.uploadedDate,
							valueId,
							valueStatus: valueStatusById.get(valueId),
							valueTypeId: valueInfo?.typeId,
							valueReference: valueInfo?.reference,
						});
					}
				}

				if (!cancelled) setDocuments(aggregated);
			} catch (err: any) {
				const { titleMessage, contentMessage } = returnMessageError(err);
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [open, sinisterId, valueIds, statusKey, reloadToken, valueInfoById, valueStatusById]);

	const confirmDeleteDocument = (document: any) => {
		setDocumentToDelete(document);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirmed = async () => {
		if (!documentToDelete) return;

		setDeleteDialogOpen(false);
		setIsLoading(true);

		try {
			const result = await SinisterDocumentsService.removeDocumentBySinister(documentToDelete.id);
			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Alerta!!" content={`Erro: ${result.message}`} />,
				});
				return;
			}

			toastHandler.present({
				type: "success",
				position: "top-right",
				durationMs: 3000,
				messageOrContent: <ToastContent title="SUCESSO!" content="Documento excluído com sucesso." />,
			});
			setDocumentToDelete(null);
			setReloadToken((prev) => prev + 1);
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleOpenPreview = (doc: any) => {
		setIsLoading(true);
		setPreviewDoc(doc);

		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
		}

		SinisterDocumentsService.previewDocumentBySinisterValues(doc.id).then((result) => {
			setIsLoading(false);

			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					messageOrContent: "Erro ao visualizar o documento!",
					position: "top-center",
				});
				setPreviewDoc(null);
			} else {
				const blobUrl = URL.createObjectURL(result);
				setPreviewUrl(blobUrl);
			}
		});
	};

	const handleClosePreview = () => {
		setPreviewDoc(null);
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
		}
	};

	const attachmentsCount = documents.length;

	return (
		<>
			<Modal open={open} onClose={onClose}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						width: "95%",
						maxWidth: 720,
						maxHeight: "90vh",
						overflowY: "auto",
						borderRadius: 3,
					}}
				>
					<LoadingComp isopen={isLoading} />

					<Grid container justifyContent="space-between" alignItems="center">
						<Typography variant="h5" sx={{ color: "primary.main", fontWeight: 300 }}>
							Anexo(s) dos valores ({attachmentsCount})
						</Typography>

						<IconButton onClick={onClose} size="small">
							<CloseIcon />
						</IconButton>
					</Grid>

					<Divider sx={{ my: 2 }} />

					<Stack spacing={2}>
						{attachmentsCount === 0 ? (
							<Typography color="text.secondary">Nenhum anexo encontrado na página atual.</Typography>
						) : (
							<ModalDocumentValueList
								documents={documents}
								onPreview={handleOpenPreview}
								onDelete={confirmDeleteDocument}
								documentTypes={[]}
								canDelete={false}
								canDeleteDoc={(doc) => doc?.valueStatus === "open"}
								showDocTypeLabel={false}
								showValueDebitLabel={true}
							/>
						)}
					</Stack>
				</Box>
			</Modal>

			<Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
				<DialogTitle>Confirmar exclusão</DialogTitle>
				<DialogContent>
					<DialogContentText>Tem certeza que deseja excluir o anexo "{documentToDelete?.originalName || documentToDelete?.originalFilename}"?</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
						Cancelar
					</Button>
					<Button onClick={handleDeleteConfirmed} color="error" variant="contained">
						Excluir
					</Button>
				</DialogActions>
			</Dialog>

			<ModalPreviewValue open={!!previewDoc} onClose={handleClosePreview} previewDoc={previewDoc} previewUrl={previewUrl} isLoading={isLoading} />
		</>
	);
};
